import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import TodoModal from '../components/TodoModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const statusColors = {
  'new': 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-yellow-100 text-yellow-800',
  'in-review': 'bg-purple-100 text-purple-800',
  'completed': 'bg-green-100 text-green-800'
};

const priorityColors = {
  'low': 'bg-gray-100 text-gray-800',
  'medium': 'bg-blue-100 text-blue-800',
  'high': 'bg-orange-100 text-orange-800'
};

const statusLabels = {
  'new': 'New',
  'in-progress': 'In Progress',
  'in-review': 'In Review',
  'completed': 'Completed'
};

const priorityLabels = {
  'low': 'Low',
  'medium': 'Medium',
  'high': 'High'
};

export default function Dashboard() {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('my-list');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [users, setUsers] = useState([]);
  const [reassigning, setReassigning] = useState(false);

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter });
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (createdByFilter) params.append('createdBy', createdByFilter);
      if (assignedToFilter) params.append('assignedTo', assignedToFilter);

      const res = await fetch(`${API_URL}/todos?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTodos(data.todos || []);
      }
    } catch (err) {
      console.error('Failed to load todos:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, priorityFilter, createdByFilter, assignedToFilter]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/todos/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => {
    fetchTodos();
    fetchUsers();
  }, [fetchTodos]);

  const handleAddTodo = () => {
    setEditingTodo(null);
    setShowModal(true);
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    fetchTodos();
  };

  const handleDeleteTodo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    try {
      const res = await fetch(`${API_URL}/todos/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        fetchTodos();
      }
    } catch (err) {
      console.error('Failed to delete todo:', err);
    }
  };

  const handleReassign = async (todoId, newAssigneeId) => {
    setReassigning(true);
    try {
      const todo = todos.find(t => t.id === todoId);
      if (!todo) return;

      const res = await fetch(`${API_URL}/todos/${todoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: todo.title,
          description: todo.description,
          status: todo.status,
          priority: todo.priority,
          assignedTo: newAssigneeId || null,
          dueDate: todo.due_date
        })
      });

      if (res.ok) {
        fetchTodos();
      }
    } catch (err) {
      console.error('Failed to reassign todo:', err);
    } finally {
      setReassigning(false);
    }
  };

  const handleQuickStatusChange = async (todoId, newStatus) => {
    try {
      const todo = todos.find(t => t.id === todoId);
      if (!todo) return;

      const res = await fetch(`${API_URL}/todos/${todoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: todo.title,
          description: todo.description,
          status: newStatus,
          priority: todo.priority,
          assignedTo: todo.assigned_to,
          dueDate: todo.due_date
        })
      });

      if (res.ok) {
        fetchTodos();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dueDate) < today;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filterCounts = (() => {
    return {
      all: todos.length,
      'my-list': todos.filter(t => !t.assigned_to).length,
      assigned: todos.filter(t => t.assigned_to).length
    };
  })();

  return (
    <div className="px-4 py-1 sm:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome, {user?.name}! Manage your tasks and collaborate with your team.
          </p>
        </div>
        <button
          onClick={handleAddTodo}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Todo
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        {/* Filter Tabs */}
        <div className="border-b">
          <nav className="flex -mb-px">
            {[
              ...(user?.role === 'admin' ? [{ id: 'all', label: 'All', count: filterCounts.all }] : []),
              { id: 'my-list', label: 'My List', count: filterCounts['my-list'] },
              { id: 'assigned', label: 'Assigned to Me', count: filterCounts.assigned }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
                  filter === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  filter === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Status & Priority Filters */}
        <div className="p-4 flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="in-progress">In Progress</option>
              <option value="in-review">In Review</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          {user?.role === 'admin' && filter === 'all' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Created By</label>
                <select
                  value={createdByFilter}
                  onChange={e => setCreatedByFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Users</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Assigned To</label>
                <select
                  value={assignedToFilter}
                  onChange={e => setAssignedToFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Assignees</option>
                  <option value="unassigned">Unassigned</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Todo List */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-gray-500 mt-3">Loading todos...</p>
        </div>
      ) : todos.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No todos</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filter === 'my-list' ? 'You have no unassigned todos.' :
              filter === 'assigned' ? 'No todos are assigned to you.' :
              'Get started by creating your first todo.'}
          </p>
          <div className="mt-6">
            <button
              onClick={handleAddTodo}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Todo
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {todos.map(todo => (
                <tr key={todo.id} className={`hover:bg-gray-50 ${todo.status === 'completed' ? 'opacity-60' : ''}`}>
                  {/* Toggle Complete */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      onClick={() => {
                        const next = todo.status === 'completed' ? 'new' : 'completed';
                        handleQuickStatusChange(todo.id, next);
                      }}
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        todo.status === 'completed'
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-300 hover:border-indigo-500'
                      }`}
                    >
                      {todo.status === 'completed' && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </td>

                  {/* Title */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      className={`text-sm font-medium text-gray-900 hover:text-indigo-600 text-left truncate block max-w-xs ${
                        todo.status === 'completed' ? 'line-through' : ''
                      }`}
                      onClick={() => handleEditTodo(todo)}
                      title={todo.description || todo.title}
                    >
                      {todo.title}
                    </button>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <select
                      value={todo.status}
                      onChange={e => handleQuickStatusChange(todo.id, e.target.value)}
                      className={`text-xs font-medium rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColors[todo.status] || 'bg-gray-100 text-gray-800'}`}
                    >
                      <option value="new">New</option>
                      <option value="in-progress">In Progress</option>
                      <option value="in-review">In Review</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>

                  {/* Priority */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[todo.priority] || 'bg-blue-100 text-blue-800'}`}>
                      {priorityLabels[todo.priority] || 'Medium'}
                    </span>
                  </td>

                  {/* Created By */}
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                    {todo.created_by_name || '-'}
                  </td>

                  {/* Assigned To */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <select
                      value={todo.assigned_to || ''}
                      onChange={e => handleReassign(todo.id, e.target.value || null)}
                      className="text-xs font-medium rounded-full px-2 py-0.5 border-0 cursor-pointer bg-gray-100 text-gray-800"
                    >
                      <option value="">Unassigned</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </td>

                  {/* Assigned By */}
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                    {todo.assigned_by_name || '-'}
                  </td>

                  {/* Due Date */}
                  <td className={`px-3 py-2 whitespace-nowrap text-sm ${
                    isOverdue(todo.due_date) && todo.status !== 'completed' ? 'text-red-600 font-medium' : 'text-gray-600'
                  }`}>
                    {todo.due_date ? (
                      <>
                        {isOverdue(todo.due_date) && todo.status !== 'completed' && <span className="mr-1">!</span>}
                        {formatDate(todo.due_date)}
                      </>
                    ) : '-'}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleDeleteTodo(todo.id)}
                      className="text-gray-400 hover:text-red-600 p-0.5"
                      title="Delete"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Todo Modal */}
      <TodoModal
        showModal={showModal}
        onClose={() => { setShowModal(false); setEditingTodo(null); }}
        onSuccess={handleModalSuccess}
        editingTodo={editingTodo}
      />
    </div>
  );
}
